/**
 * Contrat Socket.IO consommé par le client Skyjo.
 *
 * Le serveur envoie une vue personnalisée : la valeur d'une carte cachée
 * reste toujours `null`, y compris pour le propriétaire de la grille.
 */

export type RoomStatus = "lobby" | "playing" | "round-ended" | "game-ended";
export type TurnPhase =
  | "initial-reveal"
  | "draw"
  | "swap-or-discard"
  | "reveal-after-discard"
  | "resolving"
  | "round-ended"
  | "game-ended";
export type CardState = "hidden" | "revealed" | "removed";
export type DrawSource = "deck" | "discard";
export type DiscardOrigin = "held" | "grid";

interface PickupMoveBase {
  id: string;
  playerId: string;
  sequence: number;
}

export type PickupMove =
  | (PickupMoveBase & { source: "deck"; value: null })
  | (PickupMoveBase & { source: "discard"; value: number });

interface DiscardMoveBase {
  id: string;
  playerId: string;
  sequence: number;
  /** La valeur devient publique dès son arrivée dans la défausse. */
  value: number;
  /** Indique si le vol doit commencer côté face cachée. */
  startsHidden: boolean;
}

export type DiscardMove =
  | (DiscardMoveBase & { origin: "grid"; position: number })
  | (DiscardMoveBase & { origin: "held"; position: null });

export interface ProtocolError {
  code: string;
  message: string;
  details?: unknown;
}

export type AckResponse<T = undefined> =
  | { ok: true; data: T }
  | { ok: false; error: ProtocolError };

export type Ack<T = undefined> = (response: AckResponse<T>) => void;

export type AvatarId =
  | "aurora"
  | "comete"
  | "nebuleuse"
  | "orage"
  | "prisme"
  | "quartz"
  | "recif"
  | "solstice";

export type CardTheme = "classic" | "sunset" | "lagoon" | "ember" | "violet";

export const AVATAR_IDS: AvatarId[] = [
  "aurora",
  "comete",
  "nebuleuse",
  "orage",
  "prisme",
  "quartz",
  "recif",
  "solstice",
];

export const CARD_THEMES: CardTheme[] = ["classic", "sunset", "lagoon", "ember", "violet"];

/** Carte de visite publique d'un compte, sans aucune donnée privée. */
export interface PublicProfile {
  userId: string;
  displayName: string;
  discriminator: string;
  /** `displayName#discriminator`, la forme à afficher et à rechercher. */
  handle: string;
  avatarId: AvatarId;
  cardTheme: CardTheme;
  /**
   * Empreinte de la photo de profil, absente tant qu'aucune photo n'est déposée.
   * Le client en fait l'URL `/api/avatars/{userId}/{avatarPhoto}.webp` : une
   * empreinte plutôt qu'une URL, parce que ce profil voyage dans chaque
   * `game:state`.
   */
  avatarPhoto?: string;
}

/** `null` pour un invité, qui n'existe que le temps du salon. */
export type PlayerAccount = PublicProfile | null;

export interface RoomPlayer {
  id: string;
  name: string;
  seat: number;
  connected: boolean;
  isHost: boolean;
  totalScore: number;
  account: PlayerAccount;
}

export interface RoomState {
  code: string;
  status: RoomStatus;
  hostPlayerId: string;
  selfPlayerId: string;
  players: RoomPlayer[];
  minPlayers: number;
  maxPlayers: number;
}

export interface CardView {
  /** Valeur absente pour une carte cachée ou retirée. */
  value: number | null;
  state: CardState;
  /**
   * Valeur d'une case face cachée, reçue pour sa seule grille. Elle n'alimente
   * que le coup d'œil du geste de triche : `value` reste la valeur publique, et
   * c'est elle que lit tout ce qui décide du jeu ou de l'animation.
   */
  secret?: number | null;
}

export interface GamePlayer {
  id: string;
  name: string;
  seat: number;
  connected: boolean;
  isHost: boolean;
  totalScore: number;
  roundScore: number | null;
  grid: CardView[];
  account: PlayerAccount;
  /** Parti pour de bon, là où `connected: false` n'est qu'une coupure. */
  hasLeft: boolean;
}

export interface RoundScore {
  playerId: string;
  playerName: string;
  roundScore: number;
  totalScore: number;
  doubled: boolean;
}

export interface RoundResult {
  round: number;
  finisherPlayerId: string;
  scores: RoundScore[];
}

export interface GameResult {
  winnerPlayerIds: string[];
  scores: RoundScore[];
}

export interface GameState {
  roomCode: string;
  revision: number;
  status: RoomStatus;
  round: number;
  selfPlayerId: string;
  currentPlayerId: string | null;
  phase: TurnPhase;
  deckCount: number;
  discardTop: number | null;
  /** Présente uniquement pour le joueur qui vient de piocher. */
  heldCard: number | null;
  /** Source publique pendant qu'une carte attend d'être jouée, y compris pour les adversaires. */
  drawnFrom: DrawSource | null;
  /** La carte tenue peut être défaussée au lieu d'être placée. */
  canDiscardHeld: boolean;
  /** Autorise une unique reprise du dépôt courant avant de révéler. */
  canReclaimDiscard: boolean;
  /** Dernière carte prise, avec une valeur uniquement lorsqu'elle était publique. */
  lastPickup: PickupMove | null;
  /** Dernier mouvement structuré vers la défausse, dédupliqué par son identifiant. */
  lastDiscard: DiscardMove | null;
  players: GamePlayer[];
  roundResult: RoundResult | null;
  gameResult: GameResult | null;
  lastAction: string | null;
}

export interface SessionIdentity {
  playerId: string;
  reconnectToken: string;
}

export interface JoinData extends SessionIdentity {
  room: RoomState;
  game?: GameState | null;
}

// --- Social : présence, amis et invitations ---------------------------------

export type PresenceStatus = "offline" | "online" | "in-lobby" | "in-game";

export interface FriendSummary extends PublicProfile {
  status: PresenceStatus;
  /** Code du salon de l'ami, seulement s'il accepte encore des joueurs. */
  roomCode: string | null;
  /** Vrai lorsque le salon est en attente et qu'il reste une place. */
  joinable: boolean;
  friendsSince: string;
}

export interface FriendRequestSummary {
  id: string;
  direction: "incoming" | "outgoing";
  profile: PublicProfile;
  createdAt: string;
}

export interface GameInvite {
  id: string;
  roomCode: string;
  from: PublicProfile;
  createdAt: string;
  expiresAt: string;
}

/** Vue complète poussée à chaque changement, comme `room:state` pour un salon. */
export interface SocialSnapshot {
  self: PublicProfile;
  friends: FriendSummary[];
  requests: FriendRequestSummary[];
  /** Invitations reçues et encore valables. */
  invites: GameInvite[];
}

export interface InviteSentResult {
  inviteId: string;
  userId: string;
  expiresAt: string;
}

export type InviteOutcome = "accepted" | "declined" | "expired" | "cancelled";

export interface InviteResolution {
  inviteId: string;
  outcome: InviteOutcome;
  profile: PublicProfile;
}

// --- Comptes : contrat HTTP /api --------------------------------------------

export interface AccountProfile extends PublicProfile {
  email: string;
  createdAt: string;
}

export interface AccountStats {
  gamesPlayed: number;
  gamesWon: number;
  /** Entre 0 et 1 ; vaut 0 sans partie jouée. */
  winRate: number;
  bestScore: number | null;
  averageScore: number | null;
  totalRounds: number;
  currentStreak: number;
  bestStreak: number;
}

export interface GameHistoryOpponent {
  userId: string | null;
  name: string;
  score: number;
  placement: number;
}

export interface GameHistoryEntry {
  id: string;
  roomCode: string;
  playedAt: string;
  rounds: number;
  playerCount: number;
  score: number;
  placement: number;
  won: boolean;
  opponents: GameHistoryOpponent[];
}

export interface AuthSuccess {
  user: AccountProfile;
  accessToken: string;
  /** Durée de vie du jeton d'accès, en secondes. */
  expiresIn: number;
}

export interface PublicServerConfig {
  accountsEnabled: boolean;
  minPasswordLength: number;
  avatars: AvatarId[];
  cardThemes: CardTheme[];
  /** Absent sur un serveur qui ne sait pas encore recevoir de photo. */
  avatarPhotoMaxBytes?: number;
}

export type RoomClosedReason = "host-closed" | "empty";

export interface RoomClosed {
  roomCode: string;
  reason: RoomClosedReason;
}

export interface ClientToServerEvents {
  "room:create": (payload: { name?: string }, ack: Ack<JoinData>) => void;
  "room:join": (
    payload: { code: string; name?: string; reconnectToken?: string },
    ack: Ack<JoinData>,
  ) => void;
  "room:leave": (ack: Ack) => void;
  /** Réservé à l'hôte : dissout la table pour tout le monde. */
  "room:close": (ack: Ack) => void;
  "game:start": (ack: Ack) => void;
  "game:reveal-initial": (payload: { positions: [number, number] }, ack: Ack) => void;
  "game:draw": (payload: { source: DrawSource }, ack: Ack) => void;
  "game:swap": (payload: { index: number }, ack: Ack) => void;
  "game:discard-drawn": (ack: Ack) => void;
  "game:reclaim-discard": (ack: Ack) => void;
  "game:reveal-after-discard": (payload: { index: number }, ack: Ack) => void;
  "game:next-round": (ack: Ack) => void;

  "auth:identify": (payload: { accessToken: string }, ack: Ack<SocialSnapshot>) => void;
  "auth:logout": (ack: Ack) => void;
  "social:sync": (ack: Ack<SocialSnapshot>) => void;
  "friends:search": (payload: { query: string }, ack: Ack<PublicProfile[]>) => void;
  "friends:request": (payload: { handle: string }, ack: Ack) => void;
  "friends:respond": (payload: { requestId: string; accept: boolean }, ack: Ack) => void;
  "friends:cancel": (payload: { requestId: string }, ack: Ack) => void;
  "friends:remove": (payload: { userId: string }, ack: Ack) => void;
  "invite:send": (payload: { userId: string }, ack: Ack<InviteSentResult>) => void;
  "invite:respond": (
    payload: { inviteId: string; accept: boolean },
    ack: Ack<JoinData | null>,
  ) => void;
}

export interface ServerToClientEvents {
  "room:state": (state: RoomState) => void;
  "game:state": (state: GameState) => void;
  "game:error": (error: ProtocolError) => void;
  /** La table n'existe plus : sans cet avis le plateau resterait figé. */
  "room:closed": (payload: RoomClosed) => void;
  "social:state": (snapshot: SocialSnapshot) => void;
  "invite:received": (invite: GameInvite) => void;
  "invite:resolved": (resolution: InviteResolution) => void;
  /** Le jeton d'accès a expiré : le client doit le renouveler puis réidentifier. */
  "auth:expired": () => void;
}

export interface StoredSession {
  roomCode: string;
  playerId: string;
  name: string;
  reconnectToken: string;
}

export const isProtocolError = (value: unknown): value is ProtocolError => {
  if (!value || typeof value !== "object") return false;
  const candidate = value as Partial<ProtocolError>;
  return typeof candidate.code === "string" && typeof candidate.message === "string";
};
